import { useState } from 'react'
import { Bell, BellOff, Send, RotateCcw } from 'lucide-react'
import { usePushNotifications } from '../../hooks/usePushNotifications'

const borderStyle = { borderColor: 'var(--border)' }

/**
 * PWA notification toggle + test button.
 *
 * Shows contextual copy for each state so the user knows what to do:
 * - unsupported: explain browser limitation
 * - unconfigured: server-side VAPID missing (admin note, shouldn't
 *   normally be visible in prod once Luis sets the env vars)
 * - denied: instructions to unblock in OS/browser settings
 * - default: prompt to subscribe
 * - subscribed: disable + send test buttons
 */
export function NotificationsSection() {
  const { status, loading, error, subscribe, unsubscribe, sendTest } =
    usePushNotifications()
  const [testMsg, setTestMsg] = useState<string | null>(null)

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-expect-error — iOS Safari extension
      window.navigator.standalone === true)

  const isIOS =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    // @ts-expect-error — MSStream check for modern iOS
    !window.MSStream

  const handleTest = async () => {
    setTestMsg(null)
    try {
      const r = await sendTest()
      setTestMsg(`Enviado: ${r.sent} / falhado: ${r.failed}`)
    } catch {
      setTestMsg(null)
    }
    setTimeout(() => setTestMsg(null), 6000)
  }

  return (
    <div className="bg-white rounded-lg border p-5" style={borderStyle}>
      <h2
        className="text-xs font-medium uppercase tracking-wider mb-4"
        style={{ color: 'var(--ink-muted)' }}
      >
        Notificações
      </h2>

      {/* iOS install hint — Web Push on iOS requires the app to be added
          to the Home Screen. Without that step, subscribe() throws. */}
      {isIOS && !isStandalone && (
        <div
          className="mb-4 rounded-md p-3 text-xs"
          style={{
            background: 'var(--cream-dark)',
            color: 'var(--ink-light)',
            border: '1px solid var(--border)',
          }}
        >
          <strong>No iPhone/iPad:</strong> para receber notificações tens de
          abrir em Safari, tocar em <kbd>Partilhar</kbd> e escolher{' '}
          <kbd>Adicionar ao Ecrã Principal</kbd>. Depois abre a seGuru pelo
          ícone do ecrã principal e volta aqui.
        </div>
      )}

      {loading && (
        <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
          A carregar…
        </p>
      )}

      {!loading && status === 'unsupported' && (
        <p className="text-sm" style={{ color: 'var(--ink-light)' }}>
          O teu browser não suporta notificações push. Chrome, Edge, Firefox e
          Safari recentes funcionam.
        </p>
      )}

      {!loading && status === 'unconfigured' && (
        <p className="text-sm" style={{ color: 'var(--ink-light)' }}>
          Notificações ainda não configuradas no servidor. Em breve.
        </p>
      )}

      {!loading && status === 'denied' && (
        <div className="space-y-2">
          <p className="text-sm" style={{ color: 'var(--ink-light)' }}>
            Bloqueaste as notificações anteriormente. Para activar, vai às
            definições do browser ou do sistema e autoriza seguru.pt a enviar
            notificações.
          </p>
        </div>
      )}

      {!loading && status === 'default' && (
        <div className="space-y-3">
          <p className="text-sm" style={{ color: 'var(--ink-light)' }}>
            Recebe uma notificação quando o briefing matinal estiver pronto,
            10 minutos antes de cada reunião, e quando o Postman detectar
            emails operacionais.
          </p>
          <button
            onClick={subscribe}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors"
            style={{ background: 'var(--ink)', color: 'var(--cream)' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--ink)')}
          >
            <Bell size={14} />
            Activar notificações
          </button>
        </div>
      )}

      {!loading && status === 'subscribed' && (
        <div className="space-y-3">
          <p className="text-sm" style={{ color: 'var(--ink-light)' }}>
            Notificações activas neste dispositivo.
          </p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={handleTest}
              className="flex items-center gap-2 text-sm border rounded-lg px-4 py-2 transition-colors"
              style={{ ...borderStyle, color: 'var(--ink-light)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--ink)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--ink-light)')}
            >
              <Send size={14} />
              Enviar teste
            </button>
            <button
              onClick={unsubscribe}
              className="flex items-center gap-2 text-sm border rounded-lg px-4 py-2 transition-colors"
              style={{ ...borderStyle, color: 'var(--ink-muted)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--ink-light)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--ink-muted)')}
            >
              <BellOff size={14} />
              Desactivar
            </button>
          </div>
          {testMsg && (
            <p className="text-xs" style={{ color: 'var(--accent)' }}>
              {testMsg}
            </p>
          )}
        </div>
      )}

      {error && (
        <p className="text-xs mt-3" style={{ color: '#dc2626' }}>
          <RotateCcw size={11} style={{ display: 'inline', marginRight: 4 }} />
          {error}
        </p>
      )}
    </div>
  )
}
